import React from 'react';
import { createPortal } from 'react-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { Gift, Pause, Play, RotateCcw, Volume2, VolumeX, X } from 'lucide-react';
import { useFocusTools } from '../contexts/FocusToolsContext';
import { formatCountdown } from '../utils/usePomodoro';

const WINDOW_STYLES = `
  * { box-sizing: border-box; }
  html, body { margin: 0; }
  .mini-timer {
    min-height: 100dvh; padding: 12px 16px 14px; display: flex; flex-direction: column;
    align-items: center; gap: 10px; font-family: var(--timer-font); color: var(--timer-blue);
    background: radial-gradient(ellipse at center, var(--timer-glow), transparent 75%), var(--timer-bg);
  }
  .mini-timer header { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .mini-timer header span { font-size: 9px; line-height: 1.8; color: var(--timer-pink); }
  .mini-timer h1 { font-size: 12px; line-height: 1.8; margin: 0; text-align: center; }
  .mini-timer button { font: inherit; cursor: pointer; }
  .mini-timer button:focus-visible { outline: 2px solid var(--timer-pink); outline-offset: 3px; }
  .mini-timer .mini-icon {
    width: 40px; height: 40px; display: inline-grid; place-items: center; padding: 0;
    color: var(--timer-blue); border: 0; border-radius: 12px; background: transparent;
  }
  .mini-timer .mini-icon:hover { background: var(--timer-glow); }
  .mini-timer .mini-clock { width: clamp(120px, min(65vmin, calc(100dvh - 210px)), 220px); position: relative; }
  .mini-timer[data-mode="popup"] .mini-clock { width: clamp(110px, min(65vmin, calc(100dvh - 245px)), 220px); }
  .mini-timer .mini-clock svg { display: block; width: 100%; }
  .mini-timer .mini-clock output {
    position: absolute; inset: 0; display: grid; place-items: center; font-size: clamp(18px, min(9vw, calc(16vh - 35px)), 34px);
    font-variant-numeric: tabular-nums; text-shadow: 0 0 18px var(--timer-glow);
  }
  .mini-timer .mini-clock output.mini-long { font-size: clamp(16px, min(8vw, calc(14vh - 30px)), 30px); }
  .mini-timer .mini-actions { display: flex; align-items: center; justify-content: center; gap: 12px; width: 100%; }
  .mini-timer .mini-primary {
    min-height: 44px; border: 2px solid var(--timer-blue); border-radius: 14px;
    padding: 10px 14px; font-size: 10px; line-height: 1.8; color: var(--timer-bg);
    background: var(--timer-blue); box-shadow: 0 4px 0 var(--timer-shadow);
    display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  }
  .mini-timer .mini-primary:active { transform: translateY(3px); box-shadow: none; }
  .mini-timer .mini-back { color: var(--timer-blue); background: transparent; border: 0; font-size: 9px; line-height: 1.8; padding: 10px; }
  .mini-timer .mini-reward { color: var(--timer-pink); font-size: 16px; line-height: 1.9; text-align: center; overflow-wrap: anywhere; margin: 0; }
  .mini-timer .mini-note { font-size: 9px; line-height: 1.9; color: var(--timer-pink); text-align: center; margin: 0; }
  .mini-timer .mini-victory { color: var(--timer-green); flex-shrink: 0; }
  .mini-timer .mini-complete-heading { display: flex; align-items: center; gap: 10px; margin-top: 8px; }
  .mini-timer .mini-controls { display: flex; }
  @media (prefers-reduced-motion: reduce) { .mini-timer button { transition: none; } }
`;

export default function TimerWindow() {
  const { state, remainingMs, act, windowRoot, windowMode, closeTimerWindow, setFocusOpen, soundBlocked } = useFocusTools();
  const theme = useTheme();
  if (!windowRoot) return null;
  const { timer } = state;
  const active = ['running', 'paused'].includes(timer.status);
  const finished = timer.status === 'finished';
  const focus = !active || timer.phase === 'focus';
  const previewMs = active || finished ? remainingMs : state.focusMinutes * 60000;
  const duration = active || finished ? timer.durationMs : state.focusMinutes * 60000;
  const progress = Math.max(0, Math.min(1, previewMs / duration));
  const soundLabel = soundBlocked || !state.soundEnabled ? 'Enable timer sound' : 'Mute timer sound';
  const backToPage = () => {
    window.focus();
    closeTimerWindow();
    if (active) setFocusOpen(true);
  };

  return createPortal(
    <main className="mini-timer" data-mode={windowMode} aria-label="Pop-out Pomodoro timer" style={{
      '--timer-font': theme.typography.fontFamily, '--timer-blue': theme.palette.primary.main,
      '--timer-pink': theme.palette.secondary.main, '--timer-bg': theme.palette.background.default,
      '--timer-green': theme.palette.green.main, '--timer-shadow': theme.palette.primary.dark,
      '--timer-glow': alpha(theme.palette.primary.main, 0.09),
    }}>
      <style>{WINDOW_STYLES}</style>
      <header><span>Dopatize</span><div className="mini-controls">
        <button className="mini-icon" aria-label={soundLabel} onClick={() => act('sound', { enabled: soundBlocked || !state.soundEnabled })}>
          {state.soundEnabled ? <Volume2 size={18} aria-hidden="true" /> : <VolumeX size={18} aria-hidden="true" />}
        </button>
        <button className="mini-icon" aria-label="Close pop-out timer" onClick={closeTimerWindow}><X size={18} aria-hidden="true" /></button>
      </div></header>
      {finished ? (
        <>
          <div className="mini-complete-heading"><Gift className="mini-victory" size={28} aria-hidden="true" />
            <h1>{timer.phase === 'focus' ? 'Focus complete!' : 'Break complete!'}</h1></div>
          <p className="mini-reward" role="status">{timer.phase === 'focus' ? timer.reward || 'Time for a break.' : 'Ready when you are.'}</p>
          <div className="mini-actions">
            <button className="mini-primary" onClick={() => act('start', { phase: timer.phase === 'focus' ? 'break' : 'focus' })}>
              {timer.phase === 'focus' ? 'Start break' : 'Start focus'}
            </button>
            {!timer.acknowledged && <button className="mini-back" onClick={() => act('acknowledge')}>Done</button>}
          </div>
        </>
      ) : (
        <>
          <h1>{timer.status === 'paused' ? 'Paused' : focus ? 'Focus time' : 'Break time'}</h1>
          <div className="mini-clock">
            <svg viewBox="0 0 160 160" aria-hidden="true">
              <circle cx="80" cy="80" r="70" fill="none" stroke="var(--timer-glow)" strokeWidth="5" />
              <circle cx="80" cy="80" r="70" fill="none" stroke="var(--timer-blue)" strokeWidth="5" pathLength="100"
                strokeDasharray="100" strokeDashoffset={100 - progress * 100} transform="rotate(-90 80 80)" strokeLinecap="round" />
            </svg>
            <output role="timer" className={previewMs >= 100 * 60000 ? 'mini-long' : undefined} aria-live="off" aria-label={`${focus ? 'Focus' : 'Break'} time remaining`}>{formatCountdown(previewMs)}</output>
          </div>
          <div className="mini-actions">
            <button className="mini-primary" onClick={() => act(timer.status === 'running' ? 'pause' : timer.status === 'paused' ? 'resume' : 'start')}>
              {timer.status === 'running' ? <Pause size={17} aria-hidden="true" /> : <Play size={17} aria-hidden="true" />}
              {timer.status === 'running' ? 'Pause timer' : timer.status === 'paused' ? 'Resume timer' : 'Start focus'}
            </button>
            {active && <button className="mini-icon" aria-label="Reset timer" onClick={() => act('reset')}><RotateCcw size={18} aria-hidden="true" /></button>}
          </div>
        </>
      )}
      {windowMode === 'popup' && <p className="mini-note">Keep the original tab open.</p>}
      <button className="mini-back" onClick={backToPage}>Back to page</button>
    </main>, windowRoot,
  );
}
